/**
 * API Client - Easy to understand and maintain
 */

import { HttpClient } from './http-client';

export class ApiClient {
  private http: HttpClient;

  constructor(options?: {
    baseURL?: string;
    timeout?: number;
  }) {
    const baseURL = options?.baseURL || this.getDefaultBaseURL();

    this.http = new HttpClient(baseURL, {
      timeout: options?.timeout,
    });
  }

  private getDefaultBaseURL(): string {
    let baseURL: string;
    if (typeof window !== 'undefined') {
      // Browser environment
      baseURL = (import.meta as any).env?.VITE_API_BASE_URL || '/api';
    } else {
      // Node environment (SSR)
      baseURL = process.env.VITE_API_BASE_URL || 'http://localhost:5000/api';
    }

    return baseURL;
  }

  // Direct access to HTTP client for custom requests
  public getHttpClient(): HttpClient {
    return this.http;
  }
}

// Singleton instance for easy usage
let apiClientInstance: ApiClient | null = null;

export function createApiClient(options?: {
  baseURL?: string;
  timeout?: number;
}): ApiClient {
  apiClientInstance = new ApiClient(options);
  return apiClientInstance;
}

export function getApiClient(): ApiClient {
  if (!apiClientInstance) {
    console.log('⚠️ API Client: No instance found, creating default one');
    // Create with default settings if not initialized
    apiClientInstance = new ApiClient();
  }
  return apiClientInstance;
}

/**
 * Direct access to current HTTP client instance
 * Use this in hooks and API modules to always get the 
 * most up-to-date client with the current session state.
 */
export function getHttp(): HttpClient {
  return getApiClient().getHttpClient();
}

/**
 * Auth context setter - used by App.tsx
 * Simplified for Better Auth (cookie-based).
 */
export function setAuthContext(): void {
  console.log('🔧 API Client: Refreshing API client instance');
  
  // No tokens to pass, Better Auth handles cookies automatically
  createApiClient();

  console.log('✅ API Client: Instance updated');
}

// Reset for testing or re-initialization
export function resetApiClient(): void {
  apiClientInstance = null;
}
